import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Stethoscope } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shown after a provider is newly assigned to a client who already has
 * diagnoses on file. Additive only — linking never removes or reassigns
 * other providers' diagnosis relationships.
 */
export const LinkProviderDiagnosesDialog = ({ open, onOpenChange, clientId, provider, onLinked }) => {
  const [diagnoses, setDiagnoses] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    setLoading(true);
    axios.get(`${API}/clients/${clientId}/diagnoses`).then(({ data }) => {
      const list = (Array.isArray(data) ? data : data?.diagnoses || [])
        .filter((d) => d.is_active !== false && (d.status || 'active') === 'active');
      setDiagnoses(list);
      // Pre-select every diagnosis the provider isn't linked to yet
      const sel = {};
      list.forEach((d) => {
        if (!(d.provider_ids || []).includes(provider?.id)) sel[d.id] = true;
      });
      setSelected(sel);
    }).catch(() => setDiagnoses([])).finally(() => setLoading(false));
  }, [open, clientId, provider?.id]);

  const chosen = Object.keys(selected).filter((k) => selected[k]);

  const link = async () => {
    if (!chosen.length) { onOpenChange(false); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/diagnoses/link-provider`, {
        provider_id: provider.id, diagnosis_ids: chosen,
      });
      toast.success(`${provider.name || 'Provider'} linked to ${data.linked} diagnosis${data.linked === 1 ? '' : 'es'}`);
      onLinked?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link diagnoses');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="link-provider-diagnoses-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-teal-600" /> Link Diagnoses to {provider?.name || 'New Provider'}
          </DialogTitle>
          <DialogDescription>
            This client already has diagnoses on file. Select which ones the new provider should be
            linked to. This is additive — existing provider links are not changed.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-center text-sm text-gray-500">Loading diagnoses…</div>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-1" data-testid="link-dx-list">
            {diagnoses.map((d) => {
              const already = (d.provider_ids || []).includes(provider?.id);
              return (
                <label key={d.id}
                  className={`flex items-start gap-2 p-2 rounded border text-sm ${already ? 'bg-gray-50 opacity-60' : 'hover:bg-teal-50 cursor-pointer'}`}>
                  <Checkbox
                    checked={already || !!selected[d.id]}
                    disabled={already}
                    onCheckedChange={(v) => setSelected((p) => ({ ...p, [d.id]: !!v }))}
                    data-testid={`link-dx-checkbox-${d.diagnosis_code}`}
                  />
                  <span className="min-w-0">
                    <span className="font-medium">{d.diagnosis_code}</span> — {d.diagnosis_description}
                    {d.is_primary && <Badge className="ml-1 bg-teal-100 text-teal-800 text-[10px]">Primary</Badge>}
                    {already && <span className="ml-1 text-[11px] text-gray-500">(already linked)</span>}
                  </span>
                </label>
              );
            })}
            {diagnoses.length === 0 && <div className="text-sm text-gray-500 italic p-2">No active diagnoses.</div>}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="link-dx-skip-btn">Skip</Button>
          <Button onClick={link} disabled={saving || !chosen.length}
            className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="link-dx-confirm-btn">
            {saving ? 'Linking…' : `Link ${chosen.length} Diagnos${chosen.length === 1 ? 'is' : 'es'}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkProviderDiagnosesDialog;
